import * as React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  Plus,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users2,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  cn,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type PendingStaffInviteItem,
  type StaffInviteAISuggestResponse,
  type StaffInviteRole,
  type StaffInviteRowInput,
  type StaffInviteRowStatus,
} from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { FirstRunShell } from "@/components/FirstRunShell";
import { buildFirstRunSteps, firstRunEyebrow } from "@/components/firstRunSteps";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { useTerms } from "@/hooks/useTerms";
import { SuggestPermissionsDrawer } from "@/components/SuggestPermissionsDrawer";
import { TeacherEligibilityEditor } from "@/components/TeacherEligibilityEditor";
import {
  type EligibilityRowDraft,
  eligibilityDraftsToPayload,
  isEligibilityRowValid,
  newEligibilityDraft,
} from "@/components/teacher-eligibility-helpers";
import { useSections, useSubjects } from "@/hooks/useAcademicCatalogue";
import {
  usePendingStaffInvites,
  usePreviewStaffInvites,
  useRevokeStaffInvite,
  useSendStaffInvites,
  useStaffInviteAISuggest,
} from "@/hooks/useStaffInvites";
import { useTeamForm } from "@/hooks/useTeamForm";

interface DraftRow {
  id: string;
  email: string;
  full_name: string;
  role: StaffInviteRole;
  designation: string;
  /** Phase G0 — populated only when role=teacher. The wizard always
   * keeps at least one (possibly empty) row visible so the editor
   * doubles as the "add a subject" empty state. */
  eligibility: EligibilityRowDraft[];
  /**
   * Phase H3 — explicit permission overrides for ``role=staff`` rows.
   * Staff now ships with ZERO defaults, so this is the ONLY way the
   * redeemed user gets any capability. The wizard surfaces the
   * "Suggest permissions" drawer to populate this; the admin can also
   * revoke individual entries via the chip row.
   *
   * Kept on every row (including teachers) for symmetry — teachers
   * still inherit their role default set, and overrides on top are a
   * nice-to-have we may surface in a later iteration. For H3 the
   * "Suggest permissions" CTA is staff-only (teachers already have a
   * sensible default; suggesting on top would dilute the editor's
   * focus on subject + grade configuration which is the actual
   * teacher gate).
   */
  permission_overrides: string[];
  phone: string;
  custom: Record<string, string>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_LABELS: Record<StaffInviteRowStatus, string> = {
  valid: "Ready",
  duplicate_in_payload: "Duplicate",
  already_invited: "Already invited",
  already_member: "Already a member",
  invalid_email: "Invalid email",
  invalid_role: "Invalid role",
  missing_eligibility: "Add subject + grade",
  incomplete: "Missing details",
};

const STATUS_VARIANTS: Record<StaffInviteRowStatus, "success" | "warn" | "danger"> = {
  valid: "success",
  duplicate_in_payload: "warn",
  already_invited: "warn",
  already_member: "danger",
  invalid_email: "danger",
  invalid_role: "danger",
  missing_eligibility: "warn",
  incomplete: "warn",
};

let _draftIdSeq = 0;
function newDraft(initial?: Partial<DraftRow>): DraftRow {
  _draftIdSeq += 1;
  return {
    id: `draft-${_draftIdSeq}`,
    email: "",
    full_name: "",
    role: "teacher",
    designation: "",
    // Phase G0 — every teacher row needs at least one eligibility entry.
    // Seed an empty row so the editor renders as the "add a subject"
    // empty state without requiring an initial click.
    eligibility: [newEligibilityDraft()],
    // Phase H3 — empty-by-default; the "Suggest permissions" drawer
    // is the canonical entry point for populating this set. The send
    // payload omits the field when the array is empty so the BE
    // applies the role's defaults (zero for staff post-H3).
    permission_overrides: [],
    phone: "",
    custom: {},
    ...initial,
  };
}

/**
 * Phase C1b — IA first-run wizard step 2: Invite Staff.
 *
 * UX: the principal sees an editable table of draft rows (one per
 * teacher / staff member). They can add rows, edit inline, and click
 * "Validate" to receive per-row verdicts from the BE. "Send invites"
 * commits the valid rows — partial-success per the BE contract. Below
 * the editor, an "Already invited" panel lists pending invites
 * persisted across renders + offers a per-row revoke.
 *
 * Skip path: the shell renders a "Skip & finish setup" pill in the
 * top bar; that calls /me/first-run/complete and lands on /dashboard.
 * The page-level secondary CTA "Continue without inviting" mirrors
 * the same action so the principal isn't forced to scroll back up.
 */
export function FirstRunInviteStaffPage() {
  const navigate = useNavigate();
  const { firstRunComplete, status } = useAuth();

  // SC Foundation (F1) — study centres have no "teachers": there's a
  // single "team member" role, so we hide the Teacher/Staff role picker
  // + the teacher-eligibility editor and coerce every invite to `staff`.
  // The step rail + header copy also read "team", not "teachers and
  // staff". School / coaching are unchanged (role picker + eligibility
  // editor intact). `vertical` may resolve a beat after mount; the UI is
  // keyed off `isStudyCentre` in render so it settles without stale rows.
  const { vertical } = useEnabledModules();
  const resolvedVertical = vertical ?? "school";
  const isStudyCentre = resolvedVertical === "study_centre";
  const { ct, roleLabel, terms } = useTerms(resolvedVertical);
  const steps = buildFirstRunSteps(resolvedVertical);

  // ALL hooks must run on every render before any early return — see
  // React's rules-of-hooks. The wizard re-entry guard lives below the
  // hook block and short-circuits via Navigate.
  const [draftRows, setDraftRows] = React.useState<DraftRow[]>(() => [newDraft()]);
  const [previewByRow, setPreviewByRow] = React.useState<
    Record<number, { status: StaffInviteRowStatus; message?: string | null }>
  >({});
  const [topLevelError, setTopLevelError] = React.useState<string | null>(null);
  const [lastSentSummary, setLastSentSummary] = React.useState<string | null>(null);

  const previewMutation = usePreviewStaffInvites();
  const sendMutation = useSendStaffInvites();
  const pendingQuery = usePendingStaffInvites();
  // Phase G0 — academic catalogue feeds the teacher eligibility editor.
  // Cached for 5 minutes; the IA almost never edits subjects mid-wizard.
  const subjectsQuery = useSubjects();
  const sectionsQuery = useSections();
  // Phase H3 — the suggest mutation is shared across all rows; we
  // disambiguate which row it's targeting via `suggestState.rowId` so
  // a parallel click on a different row's "Suggest" button cancels
  // the in-flight call (which is the right UX — only one drawer can
  // be open at a time anyway).
  const suggestMutation = useStaffInviteAISuggest();
  const teamFormQuery = useTeamForm();
  const [suggestState, setSuggestState] = React.useState<{
    rowId: string | null;
    response: StaffInviteAISuggestResponse | null;
    error: string | null;
  }>({ rowId: null, response: null, error: null });

  // Wizard re-entry guard: if the principal already completed the
  // wizard, ship them to /dashboard rather than letting them re-issue
  // staff invites here. The proper surface for that is the (yet to
  // ship) Phase 8 user-management area.
  const shouldRedirect = status === "authed" && firstRunComplete === true;
  if (shouldRedirect) {
    return <Navigate to="/dashboard" replace />;
  }

  const updateRow = (id: string, patch: Partial<DraftRow>) => {
    setDraftRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setPreviewByRow({});
  };

  const removeRow = (id: string) => {
    setDraftRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));
    setPreviewByRow({});
  };

  const addRow = () => {
    setDraftRows((prev) => [...prev, newDraft()]);
  };

  const teamFields = teamFormQuery.data?.fields ?? [];
  const teamField = (key: string) => teamFields.find((item) => item.key === key);
  const teamVisible = (key: string) => teamField(key)?.visible ?? key !== "phone";
  const teamRequired = (key: string) => Boolean(teamField(key)?.required);
  const teamLabel = (key: string, fallback: string) => teamField(key)?.label ?? fallback;
  const visibleCustom = (teamFormQuery.data?.custom_fields ?? []).filter((item) => item.visible);

  /**
   * Phase H3 — open the suggest-permissions drawer for `row`. We
   * require a label (designation) before firing the AI so the
   * suggestion isn't generated against an empty job-title — that's
   * how we keep the AI bill bounded and the responses meaningful.
   * Falls back to the typed `full_name` if the admin hasn't filled in
   * the designation yet (better than blocking on a tooltip).
   */
  const openSuggest = async (row: DraftRow) => {
    setTopLevelError(null);
    const label = row.designation.trim() || row.full_name.trim();
    if (!label) {
      setTopLevelError(
        ct(
          "Add a designation (or at least a name) so the AI knows what to suggest for this staff member.",
        ),
      );
      return;
    }
    if (!isStudyCentre && row.role !== "staff") {
      // Defensive — UI gates the button, but if a bug fires through,
      // refuse silently so we don't burn AI budget on a teacher row
      // that has its own configured ceiling already. Study centres only
      // ever have `staff`-shaped rows, so the guard is a no-op there.
      return;
    }
    setSuggestState({ rowId: row.id, response: null, error: null });
    try {
      const response = await suggestMutation.mutateAsync({
        label,
        role: "staff",
      });
      setSuggestState({ rowId: row.id, response, error: null });
    } catch (err) {
      const message =
        err instanceof ApiError && err.code === "AI_UNAVAILABLE"
          ? "AI is temporarily unavailable. Try again in a moment, or grant permissions manually after closing this drawer."
          : err instanceof ApiError && err.message
            ? err.message
            : "Could not get an AI suggestion.";
      setSuggestState({ rowId: row.id, response: null, error: message });
    }
  };

  /**
   * Apply the AI's curated set onto the targeted row's
   * `permission_overrides`, preserving any prior overrides the admin
   * had already added (additive merge) and de-duping. Closing the
   * drawer happens inside `<SuggestPermissionsDrawer>` itself.
   */
  const applySuggestion = (rowId: string, suggestion: string[]) => {
    setDraftRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const merged = new Set(r.permission_overrides);
        for (const perm of suggestion) merged.add(perm);
        return { ...r, permission_overrides: Array.from(merged) };
      }),
    );
    setSuggestState({ rowId: null, response: null, error: null });
  };

  const closeSuggest = () => {
    setSuggestState({ rowId: null, response: null, error: null });
  };

  const removeOverride = (rowId: string, permission: string) => {
    setDraftRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, permission_overrides: r.permission_overrides.filter((p) => p !== permission) }
          : r,
      ),
    );
  };

  const clearOverrides = (rowId: string) => {
    setDraftRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, permission_overrides: [] } : r)),
    );
  };

  // Light client-side validation so the BE doesn't see obviously-broken
  // inputs (empty email, missing name). Server is still authoritative.
  const filledRows = draftRows
    .map((r, idx) => ({ ...r, idx }))
    .filter((r) => r.email.trim().length > 0 || r.full_name.trim().length > 0);
  const hasRows = filledRows.length > 0;
  const allFilledValid = filledRows.every((r) => {
    const baseValid =
      EMAIL_RE.test(r.email.trim()) &&
      r.full_name.trim().length > 0 &&
      r.full_name.trim().length <= 160;
    if (!baseValid) return false;
    // Phase G0: teachers MUST have at least one valid eligibility row.
    // Staff rows ignore the eligibility editor entirely. SC Foundation
    // (F1): study centres never have teacher rows, so skip the gate.
    if (
      teamVisible("designation") &&
      teamRequired("designation") &&
      r.designation.trim().length === 0
    ) {
      return false;
    }
    if (visibleCustom.some((field) => field.required && !(r.custom[field.key] ?? "").trim())) {
      return false;
    }
    if (!isStudyCentre && r.role === "teacher") {
      return r.eligibility.some(isEligibilityRowValid);
    }
    return true;
  });

  const toPayload = (): StaffInviteRowInput[] =>
    filledRows.map((r) => {
      // SC Foundation (F1) — coerce every study-centre invite to `staff`
      // (no teacher role exists) and never attach teacher eligibility.
      const effectiveRole: StaffInviteRole = isStudyCentre ? "staff" : r.role;
      const base: StaffInviteRowInput = {
        email: r.email.trim(),
        full_name: r.full_name.trim(),
        role: effectiveRole,
        designation: teamVisible("designation") ? r.designation.trim() || null : null,
        phone: teamVisible("phone") ? r.phone.trim() || null : null,
      };
      const customFields: Record<string, string> = {};
      for (const field of visibleCustom) {
        const value = (r.custom[field.key] ?? "").trim();
        if (value) customFields[field.key] = value;
      }
      if (Object.keys(customFields).length > 0) {
        base.custom_fields = customFields;
      }
      if (effectiveRole === "teacher") {
        base.eligibility_payload = eligibilityDraftsToPayload(r.eligibility);
      }
      // Phase H3 — only ship overrides when the admin has actually
      // curated some. Sending an empty array is also accepted by the
      // BE (it means "use defaults") but omitting keeps the wire
      // payload tight and is unambiguous on the audit log.
      if (r.permission_overrides.length > 0) {
        base.permission_overrides = [...r.permission_overrides];
      }
      return base;
    });

  const onValidate = async () => {
    setTopLevelError(null);
    if (!hasRows) {
      setTopLevelError("Add at least one row to validate.");
      return;
    }
    if (!allFilledValid) {
      setTopLevelError("Fix the highlighted rows before validating.");
      return;
    }
    try {
      const result = await previewMutation.mutateAsync(toPayload());
      const indexed: Record<number, { status: StaffInviteRowStatus; message?: string | null }> = {};
      result.items.forEach((item) => {
        const draftIdx = filledRows[item.row_index]?.idx;
        if (draftIdx !== undefined) {
          indexed[draftIdx] = { status: item.status, message: item.message };
        }
      });
      setPreviewByRow(indexed);
    } catch (err) {
      setTopLevelError(messageFor(err));
    }
  };

  const onSend = async () => {
    setTopLevelError(null);
    setLastSentSummary(null);
    if (!hasRows) {
      setTopLevelError("Add at least one row to send.");
      return;
    }
    if (!allFilledValid) {
      setTopLevelError("Fix the highlighted rows before sending.");
      return;
    }
    try {
      const result = await sendMutation.mutateAsync(toPayload());
      const sentCount = result.sent.length;
      const skippedCount = result.skipped.length;
      setLastSentSummary(
        sentCount > 0
          ? `Sent ${sentCount} invite${sentCount === 1 ? "" : "s"}.${
              skippedCount > 0 ? ` Skipped ${skippedCount}.` : ""
            }`
          : "All rows were skipped — review the verdicts and try again.",
      );
      // Clear the editor on success so the principal can either add
      // more rows or move on.
      if (sentCount > 0) {
        setDraftRows([newDraft()]);
        setPreviewByRow({});
      } else if (skippedCount > 0) {
        // Surface skip reasons inline.
        const indexed: Record<number, { status: StaffInviteRowStatus; message?: string | null }> =
          {};
        result.skipped.forEach((item) => {
          const draftIdx = filledRows[item.row_index]?.idx;
          if (draftIdx !== undefined) {
            indexed[draftIdx] = { status: item.status, message: item.message };
          }
        });
        setPreviewByRow(indexed);
      }
    } catch (err) {
      setTopLevelError(messageFor(err));
    }
  };

  const onContinue = () => {
    // C1c — advance to import-students rather than completing the
    // wizard. The "Skip & finish setup" pill in the shell still flips
    // first_run_complete and lands on /dashboard for principals who
    // want to bail mid-wizard.
    navigate("/first-run/import-students");
  };

  return (
    <FirstRunShell
      steps={steps}
      activeStep="invite_staff"
      eyebrow={firstRunEyebrow(resolvedVertical, "invite_staff")}
    >
      <header className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 grid gap-3 motion-safe:duration-500">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          {isStudyCentre ? "Invite your team" : ct("Invite your first teachers and staff")}
        </h1>
        <p className="text-muted-foreground max-w-xl text-sm leading-relaxed sm:text-base">
          Add rows for each person you want to onboard. They'll receive an email with a link to set
          their password and sign in. You can come back and invite more later from Settings.
        </p>
      </header>

      <Card className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 mt-8 motion-safe:duration-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users2 className="size-4" aria-hidden="true" /> New invites
          </CardTitle>
          <CardDescription>
            Up to 200 rows per send. Duplicate emails and existing members are skipped
            automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-3">
            {draftRows.map((row, idx) => {
              const filled = row.email.trim() || row.full_name.trim();
              const verdict = previewByRow[idx];
              const emailValid = !row.email.trim() || EMAIL_RE.test(row.email.trim());
              const teacherEligibilityMissing =
                !isStudyCentre &&
                row.role === "teacher" &&
                !row.eligibility.some(isEligibilityRowValid);
              return (
                <div
                  key={row.id}
                  className={cn(
                    "grid gap-3 rounded-md border p-3",
                    !emailValid && "border-destructive/40 bg-destructive/5",
                  )}
                >
                  <div
                    className={cn(
                      "grid gap-3 sm:items-start",
                      isStudyCentre
                        ? "sm:grid-cols-[2fr_2fr_1.5fr_auto]"
                        : "sm:grid-cols-[2fr_2fr_1.2fr_1.5fr_auto]",
                    )}
                  >
                    <div className="grid gap-1.5">
                      <Label htmlFor={`${row.id}-email`} className="text-xs">
                        {teamLabel("email", "Email")}
                      </Label>
                      <Input
                        id={`${row.id}-email`}
                        type="email"
                        autoComplete="off"
                        placeholder={isStudyCentre ? "team@example.com" : "teacher@example.com"}
                        value={row.email}
                        onChange={(e) => updateRow(row.id, { email: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor={`${row.id}-name`} className="text-xs">
                        {teamLabel("full_name", "Full name")}
                      </Label>
                      <Input
                        id={`${row.id}-name`}
                        placeholder="Asha Sharma"
                        value={row.full_name}
                        onChange={(e) => updateRow(row.id, { full_name: e.target.value })}
                      />
                    </div>
                    {/* SC Foundation (F1) — study centres have a single
                        "team member" role, so the Teacher/Staff picker is
                        hidden and every invite is coerced to `staff`. */}
                    {isStudyCentre ? null : (
                      <div className="grid gap-1.5">
                        <Label htmlFor={`${row.id}-role`} className="text-xs">
                          Role
                        </Label>
                        <Select
                          value={row.role}
                          onValueChange={(v) => updateRow(row.id, { role: v as StaffInviteRole })}
                        >
                          <SelectTrigger id={`${row.id}-role`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="teacher">
                              {roleLabel("teacher", "Teacher")}
                            </SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {teamVisible("designation") ? (
                      <div className="grid gap-1.5">
                        <Label htmlFor={`${row.id}-designation`} className="text-xs">
                          {teamLabel("designation", "Designation")}
                          {teamRequired("designation") ? null : (
                            <span className="text-muted-foreground"> (optional)</span>
                          )}
                        </Label>
                        <Input
                          id={`${row.id}-designation`}
                          placeholder={isStudyCentre ? "Front desk" : "Class Teacher"}
                          value={row.designation}
                          onChange={(e) => updateRow(row.id, { designation: e.target.value })}
                        />
                      </div>
                    ) : null}
                    <div className="flex items-end justify-between gap-2 sm:flex-col sm:justify-start">
                      {filled && verdict ? (
                        <Badge
                          variant={STATUS_VARIANTS[verdict.status]}
                          title={verdict.message ?? undefined}
                        >
                          {STATUS_LABELS[verdict.status]}
                        </Badge>
                      ) : filled && teacherEligibilityMissing ? (
                        <Badge
                          variant={STATUS_VARIANTS.missing_eligibility}
                          title="Add at least one subject + grade for this teacher"
                        >
                          {STATUS_LABELS.missing_eligibility}
                        </Badge>
                      ) : (
                        <span className="block min-h-[1.5rem]" aria-hidden />
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove row"
                        onClick={() => removeRow(row.id)}
                        disabled={draftRows.length === 1}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>

                  {teamVisible("phone") || visibleCustom.length > 0 ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {teamVisible("phone") ? (
                        <div className="grid gap-1.5">
                          <Label htmlFor={`${row.id}-phone`} className="text-xs">
                            {teamLabel("phone", "Phone")}
                          </Label>
                          <Input
                            id={`${row.id}-phone`}
                            value={row.phone}
                            onChange={(e) => updateRow(row.id, { phone: e.target.value })}
                          />
                        </div>
                      ) : null}
                      {visibleCustom.map((field) => (
                        <div key={field.key} className="grid gap-1.5">
                          <Label htmlFor={`${row.id}-${field.key}`} className="text-xs">
                            {field.label}
                            {field.required ? null : (
                              <span className="text-muted-foreground"> (optional)</span>
                            )}
                          </Label>
                          <Input
                            id={`${row.id}-${field.key}`}
                            value={row.custom[field.key] ?? ""}
                            onChange={(e) =>
                              updateRow(row.id, {
                                custom: { ...row.custom, [field.key]: e.target.value },
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Phase G0 — eligibility editor for teachers only.
                      Staff rows skip this section entirely. SC Foundation
                      (F1): study centres have no teachers, so it never
                      renders there. */}
                  {!isStudyCentre && row.role === "teacher" && (
                    <TeacherEligibilityEditor
                      rows={row.eligibility}
                      onChange={(next) => updateRow(row.id, { eligibility: next })}
                      subjects={subjectsQuery.data?.items ?? []}
                      sections={sectionsQuery.data?.items ?? []}
                      branchId={null}
                    />
                  )}

                  {/* Phase H3 — staff-only permission editor block.
                      Staff ships with zero defaults post-H3, so the
                      admin needs an explicit way to grant capability.
                      We surface the AI-suggest CTA + the chip row of
                      currently-curated overrides. Teachers and
                      higher-ceiling roles continue to inherit their
                      default set; this block does NOT render for
                      them to keep the editor focused on the actual
                      teacher gate (subject + grade eligibility). */}
                  {isStudyCentre || row.role === "staff" ? (
                    <StaffPermissionsBlock
                      row={row}
                      isStudyCentre={isStudyCentre}
                      onSuggest={() => {
                        void openSuggest(row);
                      }}
                      onRemove={(perm) => removeOverride(row.id, perm)}
                      onClear={() => clearOverrides(row.id)}
                      isSuggesting={suggestMutation.isPending && suggestState.rowId === row.id}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              <Plus className="size-4" aria-hidden="true" />
              Add another row
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onValidate}
                disabled={previewMutation.isPending || sendMutation.isPending}
              >
                {previewMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Validating…
                  </>
                ) : (
                  "Validate"
                )}
              </Button>
              <Button
                type="button"
                onClick={onSend}
                disabled={
                  sendMutation.isPending || previewMutation.isPending || !hasRows || !allFilledValid
                }
              >
                {sendMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="size-4" aria-hidden="true" />
                    Send {filledRows.length || ""} invite{filledRows.length === 1 ? "" : "s"}
                  </>
                )}
              </Button>
            </div>
          </div>

          {(topLevelError || lastSentSummary) && (
            <div
              role="status"
              className={cn(
                "rounded-md border px-3 py-2 text-sm",
                topLevelError
                  ? "border-destructive/50 bg-destructive/5 text-destructive"
                  : "border-primary/40 bg-primary/5 text-foreground",
              )}
            >
              {topLevelError ?? lastSentSummary}
            </div>
          )}
        </CardContent>
      </Card>

      <PendingInvitesPanel pending={pendingQuery.data?.items ?? []} />

      <div className="motion-safe:animate-in motion-safe:fade-in mt-10 grid gap-3 motion-safe:duration-700">
        <Button
          size="lg"
          onClick={onContinue}
          className="group mx-auto w-full max-w-sm gap-2 transition-transform duration-150 ease-out active:scale-[0.97]"
        >
          {(pendingQuery.data?.total ?? 0) > 0
            ? `Continue to import ${terms.learner.many.toLowerCase()}`
            : "Continue without inviting"}
          <ArrowRight
            className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Button>
        <p className="text-muted-foreground mx-auto max-w-md text-center text-xs leading-relaxed">
          You can invite more teammates anytime from Settings → People.
        </p>
      </div>

      {/* Phase H3 — single drawer instance, parameterised on the
          targeted row. Lives outside the row map so opening a
          different row's drawer doesn't unmount/remount the picker
          (that would lose the in-flight selection if the user
          mid-clicks). */}
      {(() => {
        const targetedRow = draftRows.find((r) => r.id === suggestState.rowId);
        if (!targetedRow) return null;
        const isOpen = Boolean(suggestState.rowId);
        const isLoading = suggestMutation.isPending && suggestState.rowId === targetedRow.id;
        const status: "idle" | "loading" | "success" | "error" = isLoading
          ? "loading"
          : suggestState.error
            ? "error"
            : suggestState.response
              ? "success"
              : "idle";
        const label =
          targetedRow.designation.trim() || targetedRow.full_name.trim() || ct("this staff member");
        return (
          <SuggestPermissionsDrawer
            open={isOpen}
            onOpenChange={(next) => {
              if (!next) closeSuggest();
            }}
            title={`Suggest permissions for ${label}`}
            subtitle={ct(
              "The AI proposes access within what staff can be given. Uncheck anything you don't want.",
            )}
            status={status}
            suggestions={suggestState.response?.suggested_overrides ?? []}
            rationales={suggestState.response?.rationales ?? {}}
            ceilingTruncated={suggestState.response?.ceiling_truncated ?? []}
            unknownDropped={suggestState.response?.unknown_dropped ?? []}
            provider={suggestState.response?.provider}
            model={suggestState.response?.model}
            fallbackDepth={suggestState.response?.fallback_depth ?? 0}
            parseError={suggestState.response?.parse_error ?? null}
            errorMessage={suggestState.error}
            onRetry={() => {
              void openSuggest(targetedRow);
            }}
            onApply={(picked) => applySuggestion(targetedRow.id, picked)}
          />
        );
      })()}
    </FirstRunShell>
  );
}

interface PendingInvitesPanelProps {
  pending: PendingStaffInviteItem[];
}

function PendingInvitesPanel({ pending }: PendingInvitesPanelProps) {
  const { roleLabel } = useTerms();
  const revokeMutation = useRevokeStaffInvite();
  if (pending.length === 0) return null;
  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="text-primary size-4" aria-hidden="true" />
          Pending invites ({pending.length})
        </CardTitle>
        <CardDescription>
          We've emailed each of these. Revoke a row if you sent it by mistake — the link stops
          working immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {pending.map((inv) => (
          <div
            key={inv.id}
            className="bg-muted/30 flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{inv.full_name}</p>
              <p className="text-muted-foreground truncate text-xs">
                {inv.email} · {roleLabel(inv.role, humanRole(inv.role))}
                {inv.designation ? ` · ${inv.designation}` : ""}
              </p>
            </div>
            {inv.last_email_error ? (
              <Badge variant="danger" title={inv.last_email_error}>
                Email failed
              </Badge>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => revokeMutation.mutate(inv.id)}
              disabled={revokeMutation.isPending}
            >
              <X className="size-3.5" aria-hidden="true" />
              Revoke
            </Button>
          </div>
        ))}
        <Separator />
        <p className="text-muted-foreground text-xs">Invites expire 72 hours after they're sent.</p>
      </CardContent>
    </Card>
  );
}

function humanRole(role: string): string {
  switch (role) {
    case "teacher":
      return "Teacher";
    case "staff":
      return "Staff";
    case "admin":
      return "Administrator";
    case "owner":
      return "Owner";
    default:
      return "Team member";
  }
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message || "Something went wrong. Try again in a moment.";
  }
  return "Something went wrong. Try again in a moment.";
}

// ── Phase H3 — staff permissions sub-block ───────────────────────────────

interface StaffPermissionsBlockProps {
  row: DraftRow;
  /** SC Foundation (F1) — relabel "Staff" copy → "Team members". */
  isStudyCentre?: boolean;
  onSuggest: () => void;
  onRemove: (permission: string) => void;
  onClear: () => void;
  isSuggesting: boolean;
}

/**
 * Phase H3 — staff-only permission editor block. Renders below each
 * staff invite row.
 *
 * UX states:
 *   - Empty (no overrides yet) — surface a calm "no permissions yet"
 *     callout with the AI suggest CTA + a hint that staff ships with
 *     zero defaults so the principal doesn't think we're hiding
 *     capabilities.
 *   - Populated — chip row of currently-curated overrides with
 *     individual remove buttons + a "Clear all" button + the AI
 *     suggest CTA labelled "Suggest more".
 *
 * The actual checkbox + bulk + apply UX lives inside
 * <SuggestPermissionsDrawer/>, opened by the parent on click.
 */
function StaffPermissionsBlock({
  row,
  isStudyCentre = false,
  onSuggest,
  onRemove,
  onClear,
  isSuggesting,
}: StaffPermissionsBlockProps) {
  const hasOverrides = row.permission_overrides.length > 0;
  const noun = isStudyCentre ? "Team members" : "Staff";
  return (
    <div
      className={cn(
        "border-border/60 rounded-md border-2 border-dashed p-3",
        // Subtle violet wash so the AI surface is visually grouped
        // even when the row itself is white.
        hasOverrides ? "border-primary/30 bg-primary/5" : "bg-muted/20",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-foreground flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
            <ShieldCheck className="size-3.5" aria-hidden="true" />
            Permissions
          </p>
          <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
            {hasOverrides
              ? `${row.permission_overrides.length} permission${row.permission_overrides.length === 1 ? "" : "s"} curated. ${noun} have no defaults — these are the only capabilities this account will receive.`
              : `${noun} start with no permissions. Use the AI suggester or grant manually to give this account capabilities.`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {hasOverrides ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onClear}
              data-testid={`staff-perms-clear-${row.id}`}
            >
              <X className="size-3.5" aria-hidden="true" />
              Clear
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onSuggest}
            disabled={isSuggesting}
            data-testid={`staff-perms-suggest-${row.id}`}
            className={cn(
              "gap-1.5",
              "border-violet-300/60 text-violet-800 hover:bg-violet-50",
              "dark:border-violet-500/40 dark:text-violet-200 dark:hover:bg-violet-950/40",
            )}
          >
            {isSuggesting ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="size-3.5" aria-hidden="true" />
            )}
            {isSuggesting ? "Thinking…" : hasOverrides ? "Suggest more" : "Suggest permissions"}
          </Button>
        </div>
      </div>
      {hasOverrides ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {row.permission_overrides.map((perm) => (
            <Badge
              key={perm}
              variant="outline"
              className="bg-background flex items-center gap-1 text-[11px] font-normal"
            >
              <span>{perm}</span>
              <button
                type="button"
                onClick={() => onRemove(perm)}
                aria-label={`Remove ${perm}`}
                className="hover:text-destructive transition-colors"
                data-testid={`staff-perms-remove-${row.id}-${perm}`}
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
